import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { parse } from 'query-string';
import { isEmpty, flatten, omit } from 'lodash-es';
import { CampaignTypeFilter } from '@dev/ee-api/ee-api-models';
import { DateDropdown, SearchInput, SimplePopover} from '@dev/ee-components';

import dayjs from '@/services/tools/dayjs';
import { debounce } from '@/services/tools/debounce';
import { LONG_DATE_FORMAT } from '@/services/constants';
import { useDateDropdownValue } from '@/services/hooks/useDateDropdownValue';
import { GetCampaigns, getCampaignsStore, UnpickAllCampaigns } from '@/store/campaigns/actions';

import { useDateDropdownOptions } from '@/components/Campaigns/hooks/useDateDropdownOptions';
import { DateDropdownOptions, getCampaignTypeOptions } from '@/components/Campaigns/CampaignsList/config';
import { TableFiltersWrapper, SearchInputWrapper, FiltersWrapper } from '../../styles';

const TableFiltersComponent = (): JSX.Element => {
    const { t } = useTranslation();
    const campaigns = useSelector(getCampaignsStore);
    const dispatch = useDispatch();
    const location = useLocation();
    const { type: queryType, search: querySearch } = parse(location.search);
    const from = campaigns?.pagination?.from;
    const to = campaigns?.pagination?.to;
    const rest = omit(campaigns.pagination, ['activePage', 'perPage']);
    const urlTypes: string[] = queryType ? flatten([queryType]) : [];

    const { type, perPage } = campaigns.pagination;
    const dateDropdownValue = useDateDropdownValue(false);

    const screenCampaignType = [CampaignTypeFilter.Draft];
    const search = campaigns?.pagination?.search || querySearch;
    
    const [selectedCampaignType, setSelectedCampaignType] = useState(urlTypes);

    const changeSearchInputVal = (event?: ChangeEvent<HTMLInputElement>): void => {
        const search = event ? event.currentTarget.value : '';

        debounce((): void => {
            dispatch(UnpickAllCampaigns());
            dispatch(GetCampaigns({ ...rest, search, limit: perPage, type: type || screenCampaignType, status: screenCampaignType }));
        }, 500);
    };

    const onChangeTypeSelection = (_ev, selected) => {
        const rest = omit(campaigns.pagination, ['activePage', 'perPage']);
        setSelectedCampaignType(selected);

        dispatch(UnpickAllCampaigns());
        dispatch(GetCampaigns({ ...rest, type: isEmpty(selected) ? screenCampaignType : selected, limit: perPage, page: 1, status: screenCampaignType }));
        
    };

    const dateFilterData = useDateDropdownOptions();

    return (
        <TableFiltersWrapper>
            <FiltersWrapper>
                <DateDropdown
                    options={dateFilterData}
                    defaultPlaceholder={t('all')}
                    value={dateDropdownValue}
                    type="input"
                    returnRawData
                    calendarOnChange={(startDate?: string, endDate?: string) => {
                        dispatch(
                            GetCampaigns({
                                ...rest,
                                type: type || screenCampaignType,
                                status: screenCampaignType,
                                limit: perPage,
                                to: endDate,
                                from: dayjs(startDate).startOf('day').format(),
                            })
                        );
                    }}
                    onItemClick={(_ev, value?: Record<string, string | number>) => {
                        dispatch(
                            GetCampaigns({
                                ...rest,
                                type: type || screenCampaignType,
                                status: screenCampaignType,
                                limit: perPage,
                                to: dayjs().endOf('day').format(LONG_DATE_FORMAT),
                                from:
                                    value?.value === DateDropdownOptions.All
                                        ? null
                                        : dayjs()
                                            .subtract(value?.value as number, 'day')
                                            .startOf('day')
                                            .format(LONG_DATE_FORMAT),
                            })
                        );
                    }}
                    buttonVariant="tertiary"
                    startDate={from?.toString()}
                    endDate={to?.toString()}
                />
                <SimplePopover
                    type="noborder"
                    options={getCampaignTypeOptions()}
                    defaultPlaceholder={t('type')}
                    id="campaignType"
                    checkboxValue={selectedCampaignType}
                    enableCheckbox
                    onCheckboxClick={onChangeTypeSelection}
                    popperProps={{
                        disablePortal: false,
                        popperOptions: {
                            fixedPosition: false,
                        },
                    }}
                    disableSentenceCase
                />
            </FiltersWrapper>
            <SearchInputWrapper>
                <SearchInput
                    id="searchCampaigns"
                    name="search"
                    placeholder={t('searchforcampaigns')}
                    type="text"
                    onChange={changeSearchInputVal}
                    onDelete={changeSearchInputVal}
                    defaultValue={search}
                />
            </SearchInputWrapper>
        </TableFiltersWrapper>
    );
};

export default TableFiltersComponent;
